import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import StyledInputErrorText from '@/components/StyledInputFields/StyledInputErrorText.tsx';
import StyledMultiSelect from '@/components/StyledInputFields/StyledMultiSelect.tsx';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import StyledTextarea from '@/components/StyledInputFields/StyledTextarea.tsx';
import useCompositionOperations from '@/hooks/api/automations/useCompositionOperations.ts';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import useRetrieveOutputContracts from '@/hooks/api/steps/useRetrieveOutputContracts.ts';
import {
  BusinessOutcomeAssessmentDeclaration,
  BusinessOutcomeAssessmentSource,
} from '@/types/automations.ts';
import { DatasetType, OutputAvailability } from '@/types/automationFields.ts';
import { Checkbox, Group, Stack, Text } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';

type FormState = {
  label: string;
  instructions: string;
  evidenceSourceKeys: string[];
  notifyOnUnfavorable: boolean;
};

type EvidenceOption = {
  value: string;
  label: string;
};

const EMPTY_FORM: FormState = {
  label: '',
  instructions: '',
  evidenceSourceKeys: [],
  notifyOnUnfavorable: false,
};

const encodeSource = (source: BusinessOutcomeAssessmentSource) => JSON.stringify([source.step_id, source.output_id]);

const decodeSource = (value: string): BusinessOutcomeAssessmentSource | null => {
  try {
    const parsed = JSON.parse(value);
    if (
      Array.isArray(parsed) &&
      parsed.length === 2 &&
      typeof parsed[0] === 'string' &&
      typeof parsed[1] === 'string'
    ) {
      return { step_id: parsed[0], output_id: parsed[1] };
    }
  } catch {
    // The values are produced locally; this simply keeps malformed form state from being submitted.
  }
  return null;
};

const formFromDeclaration = (
  declaration: BusinessOutcomeAssessmentDeclaration | null | undefined,
): FormState => {
  if (!declaration) return EMPTY_FORM;
  return {
    label: declaration.label,
    instructions: declaration.instructions,
    evidenceSourceKeys: declaration.evidence_sources.map(encodeSource),
    notifyOnUnfavorable: declaration.notify_on_unfavorable,
  };
};

const AutomationOutcomeAssessmentPanel = () => {
  const {
    data: latestVersion,
    isLoading: isLoadingVersion,
    isError: isVersionError,
  } = useRetrieveLatestAutomationVersion();
  const {
    data: outputContracts,
    isLoading: isLoadingOutputs,
    isFetching: isFetchingOutputs,
    isError: isOutputContractsError,
  } = useRetrieveOutputContracts('root');
  const { mutate, isPending, validationFeedback } = useCompositionOperations();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  useEffect(() => {
    setForm(formFromDeclaration(latestVersion?.business_outcome_assessment));
  }, [latestVersion?.id, latestVersion?.business_outcome_assessment]);

  const evidenceOptions = useMemo<EvidenceOption[]>(() => {
    const seen = new Set<string>();
    return Object.values(outputContracts?.step_output_contracts ?? {})
      .flat()
      .filter(({ contract }) => contract.dataset_type === DatasetType.Json)
      .flatMap(({ data_output, contract }) => {
        const source = {
          step_id: data_output.step_id,
          output_id: data_output.output_id ?? '',
        };
        const value = encodeSource(source);
        if (seen.has(value)) return [];
        seen.add(value);
        const outputName = data_output.output_name || 'Default output';
        return [
          {
            value,
            label: `${data_output.step_name} · ${outputName}${
              contract.availability === OutputAvailability.RuntimeOptional ? ' (may be unavailable)' : ''
            }`,
          },
        ];
      });
  }, [outputContracts]);

  const selectedSources = useMemo(
    () =>
      form.evidenceSourceKeys
        .map(decodeSource)
        .filter((source): source is BusinessOutcomeAssessmentSource => source !== null),
    [form.evidenceSourceKeys],
  );
  const eligibleEvidenceKeys = useMemo(() => new Set(evidenceOptions.map(({ value }) => value)), [evidenceOptions]);
  const hasIneligibleEvidenceSource = form.evidenceSourceKeys.some((key) => !eligibleEvidenceKeys.has(key));
  const formError =
    !form.label.trim()
      ? 'A short assessment label is required.'
      : !form.instructions.trim()
        ? 'Evaluator instructions are required.'
        : selectedSources.length === 0
          ? 'Select at least one JSON output as evidence.'
        : selectedSources.length > 5
            ? 'Select at most five JSON outputs as evidence.'
            : selectedSources.length !== form.evidenceSourceKeys.length
              ? 'One or more selected evidence sources are invalid.'
              : hasIneligibleEvidenceSource
                ? 'One or more selected evidence outputs are no longer eligible. Replace them before saving.'
              : null;
  const hasAssessment = Boolean(latestVersion?.business_outcome_assessment);
  const disabled = isPending || !latestVersion || isFetchingOutputs;

  const updateForm = (updates: Partial<FormState>) => setForm((current) => ({ ...current, ...updates }));

  const save = () => {
    if (!latestVersion || formError) return;
    mutate([
      {
        op_type: 'set_business_outcome_assessment',
        business_outcome_assessment_action: 'set',
        business_outcome_assessment: {
          schema_version: 'business_outcome_assessment.v1',
          label: form.label.trim(),
          instructions: form.instructions.trim(),
          evidence_sources: selectedSources,
          notify_on_unfavorable: form.notifyOnUnfavorable,
        },
        expected_current_value_sha256: latestVersion.business_outcome_assessment_sha256,
      },
    ]);
  };

  const clear = () => {
    if (!latestVersion) return;
    mutate([
      {
        op_type: 'set_business_outcome_assessment',
        business_outcome_assessment_action: 'clear',
        expected_current_value_sha256: latestVersion.business_outcome_assessment_sha256,
      },
    ]);
  };

  if (isLoadingVersion || isLoadingOutputs) {
    return <Text size="xs" c="dimmed">Loading assessment configuration…</Text>;
  }
  if (isVersionError || isOutputContractsError || !latestVersion) {
    return (
      <Text size="xs" c="red">
        Unable to load the version or its eligible evidence outputs. Refresh and try again.
      </Text>
    );
  }

  return (
    <Stack gap="sm">
      <Text size="xs" c="dimmed">
        Evaluate selected run evidence after completion. This does not prove an external effect or change execution
        behavior.
      </Text>
      {isFetchingOutputs && <Text size="xs" c="dimmed">Refreshing eligible evidence outputs…</Text>}
      <StyledTextInput
        label="Assessment label"
        value={form.label}
        onChange={(event) => updateForm({ label: event.currentTarget.value })}
        maxLength={80}
        disabled={disabled}
      />
      <StyledTextarea
        label="Evaluator instructions"
        description="State what counts as achieved, not achieved, and insufficient evidence."
        value={form.instructions}
        onChange={(event) => updateForm({ instructions: event.currentTarget.value })}
        minRows={4}
        maxLength={4000}
        disabled={disabled}
      />
      <StyledMultiSelect
        label="Evidence outputs"
        description="Choose one to five JSON outputs. Outputs marked “may be unavailable” can still be used; unavailable evidence is assessed as indeterminate."
        data={evidenceOptions.map(({ value, label }) => ({ value, label }))}
        value={form.evidenceSourceKeys}
        onChange={(evidenceSourceKeys) => updateForm({ evidenceSourceKeys })}
        maxValues={5}
        searchable
        disabled={disabled}
      />
      <Checkbox
        label="Notify normal run-error recipients when the outcome is not achieved or indeterminate"
        checked={form.notifyOnUnfavorable}
        onChange={(event) => updateForm({ notifyOnUnfavorable: event.currentTarget.checked })}
        disabled={disabled}
      />
      {formError && <StyledInputErrorText>{formError}</StyledInputErrorText>}
      {validationFeedback?.messages.map((message) => (
        <StyledInputErrorText key={message}>{message}</StyledInputErrorText>
      ))}
      <Group justify="space-between">
        {hasAssessment ? (
          <StyledButton variant="delete" size="sm" onClick={clear} loading={isPending} disabled={disabled}>
            Remove assessment
          </StyledButton>
        ) : (
          <span />
        )}
        <StyledButton size="sm" onClick={save} loading={isPending} disabled={disabled || !!formError}>
          Save assessment
        </StyledButton>
      </Group>
    </Stack>
  );
};

export default AutomationOutcomeAssessmentPanel;
